import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  MINUTOS_ATE_AUSENTE_PADRAO,
  estadoDe,
  haConectadoDesconhecido,
  type EstadoPresenca,
  type MembroPresenca,
  type PresencaDeMembro,
  type StatusEscolhido,
} from '@orbind/core'
import { api } from '../../lib/api.ts'
import { canalDePresenca } from './presence-client.ts'
import {
  atividadeAPartirDe,
  atividadeDaSessao,
  programaEmUsoNoSistema,
  segundosSemAtividadeNoSistema,
} from './atividade-do-sistema.ts'
import { isDesktop } from '../../lib/platform.ts'
import { useOciosidade } from './useOciosidade.ts'

/**
 * Estado de presenca do cliente.
 *
 * Sao DUAS coisas, e so duas: a lista de pessoas (permanente, do cadastro) e o
 * mapa de presencas (efemero). Grupos, contagens e ordenacao sao DERIVADOS a
 * cada render — guardar qualquer um deles criaria uma terceira verdade para
 * desincronizar.
 */

type PresenceContextValue = {
  membros: MembroPresenca[]
  presencas: ReadonlyMap<string, PresencaDeMembro>
  /** Id da pessoa logada, para tirar do menu as acoes sobre si mesma. */
  eu: string | null
  /** Como EU apareco agora (inclui o ausente automatico). */
  meuEstado: EstadoPresenca
  /** Troca a escolha e grava no cadastro. */
  escolherStatus: (status: StatusEscolhido) => void
  carregando: boolean
}

const PresenceContext = createContext<PresenceContextValue | null>(null)

export function usePresence(): PresenceContextValue {
  const valor = useContext(PresenceContext)
  if (!valor) throw new Error('usePresence precisa estar dentro de <PresenceProvider>')
  return valor
}

const CHAVE_MEMBROS = ['presence', 'members'] as const

export function PresenceProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [presencas, setPresencas] = useState<ReadonlyMap<string, PresencaDeMembro>>(() => new Map())
  const [eu, setEu] = useState<string | null>(null)
  /**
   * Minutos ate o ausente automatico, definidos no Console. Chegam pela
   * abertura do canal e mudam ao vivo quando o super admin ajusta.
   */
  const [minutosAteAusente, setMinutosAteAusente] = useState(MINUTOS_ATE_AUSENTE_PADRAO)

  // A lista completa de pessoas: buscada uma vez na carga do app.
  const { data, isPending } = useQuery({
    queryKey: CHAVE_MEMBROS,
    queryFn: async (): Promise<MembroPresenca[]> => {
      const resposta = await api.presence.members.$get()
      return (await resposta.json()).members
    },
    staleTime: Number.POSITIVE_INFINITY,
  })

  const membros = useMemo(() => data ?? [], [data])

  // Guarda a lista mais recente para o efeito do canal nao precisar dela nas
  // deps — reassinar a cada nova lista derrubaria e reabriria a conexao.
  const membrosRef = useRef(membros)
  membrosRef.current = membros

  const euRef = useRef(eu)
  euRef.current = eu

  useEffect(() => {
    canalDePresenca.reabrir()
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo === 'configuracao') {
        setMinutosAteAusente(evento.minutosAteAusente)
        return
      }

      if (evento.tipo === 'abertura') {
        setEu(evento.voce)
        setMinutosAteAusente(evento.minutosAteAusente)
        // SUBSTITUI o mapa inteiro. E isto, e so isto, que conserta o estado
        // depois de uma queda: quem entrou, saiu ou trocou de status durante a
        // queda nunca gerou aviso para este cliente.
        setPresencas(new Map(evento.presencas.map((p) => [p.memberId, p])))
        return
      }

      if (evento.tipo === 'presenca') {
        setPresencas((atual) => {
          const proximo = new Map(atual)
          if (evento.presenca.estado === 'desconectado') proximo.delete(evento.presenca.memberId)
          else proximo.set(evento.presenca.memberId, evento.presenca)
          return proximo
        })
        return
      }

      // O chat tem quem cuide dele (useChatAoVivo, useDigitando, useContatos);
      // aqui so a presenca.
      if (
        evento.tipo === 'chat' ||
        evento.tipo === 'contato' ||
        evento.tipo === 'digitando' ||
        evento.tipo === 'mencao' ||
        evento.tipo === 'mensagem' ||
        evento.tipo === 'chamada' ||
        // Notificação tem dono próprio (AvisosDeNotificacao/useNotificacoes).
        evento.tipo === 'notificacao' ||
        // A sala tem dono próprio (Videochamada); aqui não muda nada.
        evento.tipo === 'sala'
      ) {
        return
      }

      // Perfil: substitui a pessoa na lista. O cache do React Query e a fonte
      // da lista, entao escrever nele atualiza tudo que a consome de uma vez.
      queryClient.setQueryData<MembroPresenca[]>(CHAVE_MEMBROS, (anterior) =>
        (anterior ?? []).map((m) => (m.id === evento.membro.id ? evento.membro : m)),
      )
      // O cadastro tambem aparece fora do painel (autor de comentario,
      // responsavel do item). Sem invalidar, o nome antigo fica congelado no
      // que ja esta renderizado.
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'bootstrap'] })
      if (evento.membro.id === euRef.current) void queryClient.invalidateQueries({ queryKey: ['session'] })
    })
  }, [queryClient])

  /**
   * Atividade desta janela + do computador (SPEC-20260924-0924).
   *
   * Com a janela ativa, reporta na hora. Com a janela parada, ainda pode haver
   * alguém trabalhando: o desktop pergunta ao sistema de tempos em tempos, e
   * só vira "parado" quando o computador também está.
   *
   * O servidor decide o "ausente" quando TODAS as sessões da pessoa pararem.
   */
  const janelaParada = useRef(false)
  /** A janela do Orbind está sem foco (outro programa à frente) — só no desktop. */
  const janelaSemFoco = useRef(false)
  /**
   * Janela parada: pergunta ao sistema o tempo sem atividade e, se a pessoa
   * está "no computador", também o programa em uso (SPEC-20261020-2341-programa-em-uso-na-presenca).
   */
  const informarDoSistema = useCallback(async () => {
    const segundos = await segundosSemAtividadeNoSistema()
    const atividade = atividadeAPartirDe(false, segundos, minutosAteAusente)
    const programa = atividade === 'no-computador' ? await programaEmUsoNoSistema() : null
    return atividadeDaSessao(janelaParada.current, janelaSemFoco.current, { atividade, programa })
  }, [minutosAteAusente])
  useEffect(() => {
    let vivo = true
    const reportar = async () => {
      if (!vivo) return
      if (!janelaParada.current && !janelaSemFoco.current) {
        canalDePresenca.informarAtividade('no-orbind')
        return
      }
      const { atividade, programa } = await informarDoSistema()
      if (!vivo) return
      canalDePresenca.informarAtividade(atividade, programa)
    }
    /*
     * Meio minuto entre perguntas: o dado só muda de estado uma vez por vez, e
     * consultar mais que isso gastaria ida ao shell sem mudar nada na tela.
     */
    const relogio = setInterval(() => void reportar(), 30_000)
    return () => {
      vivo = false
      clearInterval(relogio)
    }
  }, [informarDoSistema])

  /*
    Desktop: a janela perder o foco já é sinal de que a pessoa foi para outro
    programa — pergunta ao sistema na hora, sem esperar o tempo de ociosidade
    (SPEC-20261020-2341-programa-em-uso-na-presenca). No navegador não: lá não se sabe o que
    acontece fora da aba, e trocar de aba não pode virar "ausente".
  */
  useEffect(() => {
    if (!isDesktop()) return
    const saiu = () => {
      janelaSemFoco.current = true
      void informarDoSistema().then(({ atividade, programa }) => canalDePresenca.informarAtividade(atividade, programa))
    }
    const voltou = () => {
      janelaSemFoco.current = false
      if (!janelaParada.current) canalDePresenca.informarAtividade('no-orbind')
    }
    const aoVisibilidade = () => (document.hidden ? saiu() : voltou())
    window.addEventListener('blur', saiu)
    window.addEventListener('focus', voltou)
    document.addEventListener('visibilitychange', aoVisibilidade)
    return () => {
      window.removeEventListener('blur', saiu)
      window.removeEventListener('focus', voltou)
      document.removeEventListener('visibilitychange', aoVisibilidade)
    }
  }, [informarDoSistema])

  useOciosidade(
    useCallback(
      (ocioso: boolean) => {
        janelaParada.current = ocioso
        if (!ocioso) {
          canalDePresenca.informarAtividade('no-orbind')
          return
        }
        // a janela acabou de parar: pergunta ao sistema sem esperar o relógio
        void informarDoSistema().then(({ atividade, programa }) => {
          canalDePresenca.informarAtividade(atividade, programa)
        })
      },
      [informarDoSistema],
    ),
    minutosAteAusente,
  )

  // Alguem se cadastrou com a sessao ja aberta: chegou id conectado que a
  // lista (vinda da carga inicial) nao conhece. Sem rebuscar, a pessoa
  // apareceria conectada e sem nome nenhum.
  useEffect(() => {
    if (isPending) return
    if (haConectadoDesconhecido(membrosRef.current, presencas)) {
      void queryClient.invalidateQueries({ queryKey: CHAVE_MEMBROS })
    }
  }, [presencas, isPending, queryClient])

  const escolherStatus = useCallback((status: StatusEscolhido) => {
    canalDePresenca.escolherStatus(status)
  }, [])

  const valor = useMemo<PresenceContextValue>(
    () => ({
      membros,
      presencas,
      eu,
      // Antes da abertura eu ainda nao sei quem sou; `disponivel` e o padrao
      // otimista e o menu corrige em milissegundos.
      meuEstado: eu === null ? 'disponivel' : estadoDe(eu, presencas),
      escolherStatus,
      carregando: isPending,
    }),
    [membros, presencas, eu, escolherStatus, isPending],
  )

  return <PresenceContext.Provider value={valor}>{children}</PresenceContext.Provider>
}
